import { memo, useEffect, useMemo } from "react";
import { Outlet } from "react-router";
import { ConfigProvider, Grid, Layout, notification, Progress } from "antd";

const { useBreakpoint } = Grid;

import HeaderLayout from "./HeaderLayout";
import { FooterLayout } from "./FooterLayout";
import FloatingActionsLayout from "./FloatingActionsLayout";
import { useGlobalStore } from "../context/useGlobalStore";
import { darkTheme, lightTheme } from "../utils/mainTheme";
import useFilterMap from "@/hooks/useFilterMap";
import useCountryCatalog from "@/hooks/useCountryCatalog";
import { useLayoutBootstrap } from "@/hooks/useLayoutBootstrap";
import { useLayoutLoader } from "@/hooks/useLayoutLoader";

notification.config({ placement: "bottomRight" });

// Isolated so its re-renders (query state changes) don't propagate to MainLayout.
const BootstrapRunner = memo(() => {
    useLayoutBootstrap();
    useCountryCatalog();
    return null;
});

const LOADER_WRAP_STYLE = {
    position: "relative", height: 3, top: -13,
    left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 5,
};
const PROGRESS_STYLE = { margin: 0 };

// Isolated so the 260ms progress ticks don't re-render MainLayout.
const HeaderLoader = memo(() => {
    const { isBusy, progressPercent } = useLayoutLoader();
    return (
        <div style={{ ...LOADER_WRAP_STYLE, visibility: isBusy ? "visible" : "hidden" }}>
            <Progress
                percent={progressPercent}
                status="active"
                showInfo={false}
                strokeLinecap="round"
                size={{ height: 2 }}
                railColor="transparent"
                strokeColor={{ "0%": "#1677ff", "100%": "#4dabf7" }}
                style={PROGRESS_STYLE}
            />
        </div>
    );
});

const MainLayout = () => {
    const theme = useGlobalStore((s) => s.theme);
    const clearFilterMap = useFilterMap((s) => s.clearAll);
    const setFilterMapState = useFilterMap((s) => s.setState);

    // useBreakpoint fires only at breakpoint boundaries, not on every pixel resize.
    // Replaces the old viewport useState + requestAnimationFrame resize listener.
    const screens = useBreakpoint();
    const isMobile = !screens.lg;

    useEffect(() => {
        clearFilterMap();
        setFilterMapState("mapMode", "global");
    }, [clearFilterMap, setFilterMapState]);

    const styles = useMemo(() => ({
        headerBlock: { position: "relative" },
        header: {
            height: "unset",
            lineHeight: "unset",
            borderBottom: "1px solid #eee",
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
            paddingInline: isMobile ? 5 : 20,
            position: "relative",
            zIndex: 10,
        },
        // CSS dvh keeps height accurate without React state — no resize listener needed
        layout: isMobile
            ? { minHeight: "unset", overflowY: "auto", display: "initial" }
            : { minHeight: "calc(100dvh - 74px)", overflowY: "auto", display: "initial" },
        content: isMobile
            ? { height: "unset", display: "flex", flexDirection: "column", paddingBlock: 5 }
            : { minHeight: "calc(100dvh - 64px)", display: "flex", flexDirection: "column", paddingBlock: 5 },
        footer: { height: "unset", lineHeight: "unset", padding: 0, backgroundColor: "#0F172A", color: "#fff" },
    }), [isMobile]);

    return (
        <ConfigProvider theme={theme === "dark" ? darkTheme : lightTheme}>
            <BootstrapRunner />
            <Layout>
                <div style={styles.headerBlock}>
                    <Layout.Header style={styles.header}>
                        <HeaderLayout />
                    </Layout.Header>
                    <HeaderLoader />
                </div>
                <Layout style={styles.layout}>
                    <Layout.Content style={styles.content}>
                        <Outlet />
                    </Layout.Content>
                    <Layout.Footer style={styles.footer}>
                        <FooterLayout />
                    </Layout.Footer>
                </Layout>
            </Layout>

            <FloatingActionsLayout />
        </ConfigProvider>
    );
};

export default memo(MainLayout);
